import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../../api";
import { DESK } from "../../constants/testIds";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { FormField } from "../common/FormField";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { formatCurrency } from "../../lib/format";
import { toast } from "../ui/sonner";

// UX-POLISH-01 #10: `create_membership` never blocked a 2nd concurrent membership for the same
// member — this modal is the missing UI for it (e.g. a member on "Standard Monthly" adding a
// separate "Personal Training" pack), with the SAME server-enforced discount cap as the walk-in
// flow (`plan.max_discount_percent`).
export function AddPlanModal({ member, open, onOpenChange }) {
  const [planId, setPlanId] = useState("");
  const [discountPercent, setDiscountPercent] = useState("0");
  const queryClient = useQueryClient();

  const plansQ = useQuery({ queryKey: ["desk-plans"], queryFn: async () => (await api.get("/plans")).data.plans, enabled: open });
  const plan = plansQ.data?.find((p) => p.id === planId);
  const maxDiscount = plan?.max_discount_percent ?? 100;

  const mutation = useMutation({
    mutationFn: () => api.post(
      "/memberships",
      {
        member_ref: member.id, plan_ref: planId, start: new Date().toISOString().slice(0, 10),
        discount_percent: Number(discountPercent) || 0,
      },
      { headers: { "Idempotency-Key": crypto.randomUUID() } },
    ),
    onSuccess: () => {
      toast.success(`${plan?.name} added to ${member?.profile?.name}'s plans.`);
      queryClient.invalidateQueries({ queryKey: ["desk-member-memberships", member.id] });
      onOpenChange(false);
      setPlanId(""); setDiscountPercent("0");
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-modal">
        <DialogHeader>
          <DialogTitle>Add another plan</DialogTitle>
          <DialogDescription>{member?.profile?.name} can hold this plan alongside their existing one(s) — e.g. a gym membership plus a separate PT pack.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Plan <span style={{ color: "var(--scarlet-neg)" }}>*</span></Label>
            <Select onValueChange={(v) => { setPlanId(v); setDiscountPercent("0"); }} value={planId}>
              <SelectTrigger data-testid={DESK.addPlanSelect}><SelectValue placeholder="Choose a plan" /></SelectTrigger>
              <SelectContent>
                {plansQ.data?.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name} — {formatCurrency(p.pricing.amount)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {plan && (
            <div className="space-y-1.5">
              <Label>Discount % <span className="text-xs font-normal" style={{ color: "#68686E" }}>(max {maxDiscount}% for this plan)</span></Label>
              <FormField
                type="number" testId={DESK.addPlanDiscountInput} value={discountPercent}
                onChange={(v) => setDiscountPercent(v === "" ? "" : String(Math.min(Number(v) || 0, maxDiscount)))}
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            data-testid={DESK.addPlanSubmitButton}
            disabled={!planId || mutation.isPending}
            onClick={() => mutation.mutate()}
            className="text-white" style={{ background: "#D6202B" }}
          >
            Add plan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
